/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {z} from 'zod';

import {isEmpty, isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {OpeningHoursResponseBody} from '@dms/api/base';
import {
  TelephonyLine,
  TelephonyLineBusinessHours,
  TelephonyLineBusinessHoursSchema,
  UpdateTelephonyLineRequestBody,
} from '@dms/api/settings';
import i18n from '@dms/i18n';

import {Nullish} from 'shared/web';

const LINE_NAME_MAX_LENGTH = 255;
export const GREETING_MAX_LENGTH = 1000;

/** Weekdays in display order, derived from the wire schema so the two can never disagree. */
export const BUSINESS_HOURS_DAYS = TelephonyLineBusinessHoursSchema.keyof().options;

export type BusinessHoursDay = (typeof BUSINESS_HOURS_DAYS)[number];

const HH_MM_LENGTH = 5;

const toHHMM = (time: string) => time.substring(0, HH_MM_LENGTH);

const toHHMMSS = (time: string) => (time.length === HH_MM_LENGTH ? `${time}:00` : time);

const hoursSlotSchema = z
  .object({
    from: z.string().nullable(),
    to: z.string().nullable(),
  })
  .superRefine((slot, ctx) => {
    const isFromSet = !isNilOrEmpty(slot.from?.trim());
    const isToSet = !isNilOrEmpty(slot.to?.trim());

    if (isFromSet !== isToSet) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('entity.branch.validations.fillBothTimesOrLeaveBothEmpty'),
        path: [isFromSet ? 'to' : 'from'],
      });
      return;
    }

    if (isNotNil(slot.from) && isNotNil(slot.to) && isFromSet && slot.from >= slot.to) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('entity.branch.validations.fromTimeMustBeBeforeToTime'),
        path: ['to'],
      });
    }
  });

export type HoursSlotValues = z.infer<typeof hoursSlotSchema>;

const daySlotsSchema = z.array(hoursSlotSchema);

const businessHoursSchema = z.object({
  monday: daySlotsSchema,
  tuesday: daySlotsSchema,
  wednesday: daySlotsSchema,
  thursday: daySlotsSchema,
  friday: daySlotsSchema,
  saturday: daySlotsSchema,
  sunday: daySlotsSchema,
});

export type BusinessHoursFormValues = z.infer<typeof businessHoursSchema>;

export const telephonyLineFormSchema = z.object({
  name: z.string().trim().min(1).max(LINE_NAME_MAX_LENGTH),
  branchId: z.string().nullable(),
  businessHours: businessHoursSchema,
  greetingIntro: z.string().trim().max(GREETING_MAX_LENGTH).nullable(),
  greetingOutOfHours: z.string().trim().max(GREETING_MAX_LENGTH).nullable(),
});

export type TelephonyLineFormValues = z.infer<typeof telephonyLineFormSchema>;

export const EMPTY_HOURS_SLOT: HoursSlotValues = {from: null, to: null};

type TimeRange = TelephonyLineBusinessHours[BusinessHoursDay][number];

/**
 * A closed day still renders one blank slot so the dealer has somewhere to type; the blank slot is
 * dropped again on the way out.
 */
const rangesToSlots = (ranges: TimeRange[] | Nullish): HoursSlotValues[] =>
  isNil(ranges) || isEmpty(ranges)
    ? [EMPTY_HOURS_SLOT]
    : ranges.map((range) => ({from: toHHMM(range.from), to: toHHMM(range.to)}));

/**
 * Both the line schedule and the branch opening hours use the same per-day range lists, so one
 * reader serves the stored line and the "copy from branch" prefill alike.
 */
export const getBusinessHoursFormValues = (
  hours: Partial<Record<BusinessHoursDay, TimeRange[] | Nullish>> | Nullish
): BusinessHoursFormValues => ({
  monday: rangesToSlots(hours?.monday),
  tuesday: rangesToSlots(hours?.tuesday),
  wednesday: rangesToSlots(hours?.wednesday),
  thursday: rangesToSlots(hours?.thursday),
  friday: rangesToSlots(hours?.friday),
  saturday: rangesToSlots(hours?.saturday),
  sunday: rangesToSlots(hours?.sunday),
});

export const getTelephonyLineFormValues = (line: TelephonyLine): TelephonyLineFormValues => ({
  name: line.name,
  branchId: line.branchId,
  businessHours: getBusinessHoursFormValues(line.businessHours),
  greetingIntro: line.greetingIntro,
  greetingOutOfHours: line.greetingOutOfHours,
});

const isSlotFilled = (slot: HoursSlotValues): slot is {from: string; to: string} =>
  isNotNil(slot.from) && isNotNil(slot.to) && !isEmpty(slot.from) && !isEmpty(slot.to);

const slotsToRanges = (slots: HoursSlotValues[]): TimeRange[] =>
  slots.filter(isSlotFilled).map((slot) => ({from: toHHMMSS(slot.from), to: toHHMMSS(slot.to)}));

export const hasAnyBusinessHours = (hours: BusinessHoursFormValues) =>
  BUSINESS_HOURS_DAYS.some((day) => hours[day].some(isSlotFilled));

/**
 * Branch opening hours are "no sales hours" when the object is missing or every day is empty —
 * there is nothing worth copying in either case.
 */
export const hasAnyOpeningHours = (hours: OpeningHoursResponseBody | Nullish) =>
  isNotNil(hours) && BUSINESS_HOURS_DAYS.some((day) => !isNilOrEmpty(hours[day]));

/**
 * A week with no slot at all means the dealer did not restrict the line, which the backend
 * expresses as `null` (always open) — an all-closed week would silence the line entirely.
 */
export const getTelephonyLineBusinessHours = (
  hours: BusinessHoursFormValues
): TelephonyLineBusinessHours | null => {
  if (!hasAnyBusinessHours(hours)) {
    return null;
  }

  return {
    monday: slotsToRanges(hours.monday),
    tuesday: slotsToRanges(hours.tuesday),
    wednesday: slotsToRanges(hours.wednesday),
    thursday: slotsToRanges(hours.thursday),
    friday: slotsToRanges(hours.friday),
    saturday: slotsToRanges(hours.saturday),
    sunday: slotsToRanges(hours.sunday),
  };
};

const emptyToNull = (value: string | Nullish) => {
  const trimmed = value?.trim();

  return isNil(trimmed) || isEmpty(trimmed) ? null : trimmed;
};

export const getUpdateTelephonyLineRequestBody = (
  values: TelephonyLineFormValues
): UpdateTelephonyLineRequestBody => ({
  name: values.name,
  branchId: values.branchId,
  businessHours: getTelephonyLineBusinessHours(values.businessHours),
  greetingIntro: emptyToNull(values.greetingIntro),
  greetingOutOfHours: emptyToNull(values.greetingOutOfHours),
});
